import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Gavel as GavelIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Token as TokenIcon,
  Payment as PaymentIcon,
  ExpandLess,
  ExpandMore,
  CloudUpload,
  ListAlt,
  Settings,
  Description,
  Business,
  Psychology as PsychologyIcon,
  SmartToy as AIIcon,
  Science as ScienceIcon,
  Upload as UploadIcon,
  Checklist as ChecklistIcon,
  AccountTree as AccountTreeIcon,
  Article as ArticleIcon,
  LibraryBooks as LibraryBooksIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

interface MenuItem {
  title: string;
  path?: string;
  icon: React.ReactNode;
  children?: MenuItem[];
}

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, canAccessFlowAdmin } = useAuth();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<{ [key: string]: boolean }>({
    pandora: true,
    jurisprudence: true,
    'inteligência artificial': true,
  });

  const fullAdminMenuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: '/admin',
      icon: <DashboardIcon />,
    },
    {
      title: 'Usuários',
      path: '/admin/users',
      icon: <PeopleIcon />,
    },
    {
      title: 'Painel do Beta',
      path: '/admin/beta',
      icon: <InsightsIcon />,
    },
    {
      title: 'Fluxos de Documentos',
      path: '/admin/document-flows',
      icon: <Description />,
    },
    {
      title: 'Pandora',
      icon: <ScienceIcon />,
      children: [
        {
          title: 'Dashboard',
          path: '/admin/pandora',
          icon: <DashboardIcon />,
        },
        {
          title: 'Upload',
          path: '/admin/pandora/upload',
          icon: <UploadIcon />,
        },
        {
          title: 'Documentos',
          path: '/admin/pandora/documents',
          icon: <ArticleIcon />,
        },
        {
          title: 'Validação',
          path: '/admin/pandora/validation',
          icon: <ChecklistIcon />,
        },
        {
          title: 'Taxonomia',
          path: '/admin/pandora/taxonomy',
          icon: <AccountTreeIcon />,
        },
        {
          title: 'Conteúdo Validado',
          path: '/admin/pandora/validated',
          icon: <LibraryBooksIcon />,
        },
      ],
    },
    {
      title: 'Jurisprudências',
      icon: <GavelIcon />,
      children: [
        {
          title: 'Importar Dados',
          path: '/admin/jurisprudence/import',
          icon: <CloudUpload />,
        },
        {
          title: 'Gerenciar',
          path: '/admin/jurisprudence/list',
          icon: <ListAlt />,
        },
      ],
    },
    {
      title: 'Comunicação',
      icon: <EmailIcon />,
      children: [
        {
          title: 'Notificações',
          path: '/admin/notifications',
          icon: <NotificationsIcon />,
        },
        {
          title: 'Campanhas Email',
          path: '/admin/email-campaigns',
          icon: <EmailIcon />,
        },
      ],
    },
    {
      title: 'Financeiro',
      icon: <PaymentIcon />,
      children: [
        {
          title: 'Créditos',
          path: '/admin/credits',
          icon: <TokenIcon />,
        },
        {
          title: 'Configurações de Pagamento',
          path: '/admin/payment-settings',
          icon: <Settings />,
        },
      ],
    },
    {
      title: 'Conteúdo',
      icon: <Description />,
      children: [
        {
          title: 'Templates',
          path: '/admin/templates',
          icon: <Business />,
        },
      ],
    },
    {
      title: 'Inteligência Artificial',
      icon: <PsychologyIcon />,
      children: [
        {
          title: 'Configurações de IA',
          path: '/admin/ai-settings',
          icon: <AIIcon />,
        },
      ],
    },
  ];

  const flowAdminMenuItems: MenuItem[] = [
    {
      title: 'Fluxos de Documentos',
      path: '/admin/document-flows',
      icon: <Description />,
    },
    {
      title: 'Pandora',
      icon: <ScienceIcon />,
      children: [
        {
          title: 'Dashboard',
          path: '/admin/pandora',
          icon: <DashboardIcon />,
        },
        {
          title: 'Upload',
          path: '/admin/pandora/upload',
          icon: <UploadIcon />,
        },
        {
          title: 'Documentos',
          path: '/admin/pandora/documents',
          icon: <ArticleIcon />,
        },
        {
          title: 'Validação',
          path: '/admin/pandora/validation',
          icon: <ChecklistIcon />,
        },
        {
          title: 'Taxonomia',
          path: '/admin/pandora/taxonomy',
          icon: <AccountTreeIcon />,
        },
        {
          title: 'Conteúdo Validado',
          path: '/admin/pandora/validated',
          icon: <LibraryBooksIcon />,
        },
      ],
    },
  ];

  const menuItems: MenuItem[] = isAdmin()
    ? fullAdminMenuItems
    : canAccessFlowAdmin()
      ? flowAdminMenuItems
      : [];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item: MenuItem) => {
    if (item.children) {
      // Toggle submenu
      setExpandedMenus(prev => ({
        ...prev,
        [item.title.toLowerCase()]: !prev[item.title.toLowerCase()],
      }));
    } else if (item.path) {
      // Navigate
      navigate(item.path);
      if (isMobile) {
        setMobileOpen(false);
      }
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Title */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 40,
            height: 40,
            fontSize: '1.2rem',
            fontWeight: 'bold',
          }}
        >
          AP
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            Admin Panel
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Advogada Parceira
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Menu Items */}
      <List sx={{ flex: 1, py: 2, px: 1.5, overflowY: 'auto' }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.title}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item)}
                selected={isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white !important',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white !important',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'white !important',
                    },
                  },
                  '&:hover': {
                    bgcolor: item.children ? 'transparent' : 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive(item.path) ? 'white' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isActive(item.path) ? 600 : 500,
                    color: isActive(item.path) ? 'white' : 'inherit',
                  }}
                />
                {item.children &&
                  (expandedMenus[item.title.toLowerCase()] ? (
                    <ExpandLess sx={{ color: isActive(item.path) ? 'white' : 'inherit' }} />
                  ) : (
                    <ExpandMore sx={{ color: isActive(item.path) ? 'white' : 'inherit' }} />
                  ))}
              </ListItemButton>
            </ListItem>

            {/* Submenu */}
            {item.children && (
              <Collapse
                in={expandedMenus[item.title.toLowerCase()]}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.title} disablePadding sx={{ mb: 0.5 }}>
                      <ListItemButton
                        onClick={() => handleMenuClick(child)}
                        selected={isActive(child.path)}
                        sx={{
                          pl: 6,
                          borderRadius: 2,
                          py: 1,
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'white !important',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white !important',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'white !important',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: isActive(child.path) ? 'white' : 'text.secondary',
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.title}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isActive(child.path) ? 600 : 500,
                            color: isActive(child.path) ? 'white' : 'inherit',
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>

      <Divider />

      {/* User Info */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Avatar
            src={user?.avatar_url}
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
            }}
          >
            {user?.name?.charAt(0) || user?.email?.charAt(0)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.name || user?.email}
            </Typography>
            <Chip label="Admin" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* AppBar - Mobile */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1,
          display: { xs: 'block', md: 'none' },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            Admin Panel
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              boxShadow: 2,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, md: 0 },
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;

